<template>
  <div>
    <!--begin::Body-->
    <div class="kt-login__body">
      <!--begin::Signin-->
      <div class="kt-login__form">
        <!--begin::Form-->
        <b-form
          class="kt-form form elevation-1"
          @submit.stop.prevent="onSubmit"
        >
          <h2 class="form-heading">PAYNOWPRO</h2>

          <div
            role="alert"
            v-bind:class="{ show: errors.length }"
            class="alert fade alert-danger"
          >
            <div class="alert-text" v-for="(error, i) in errors" :key="i">
              {{ error }}
            </div>
          </div>

          <template v-if="!mfaRequired">
            <b-form-group
              class="form-group"
              id="example-input-group-1"
              label
              label-for="example-input-1"
              ><label class="d1-block">Email Address</label>
              <b-form-input
                placeholder=""
                class="input email"
                id="example-input-1"
                name="example-input-1"
                v-model="$v.form.email.$model"
                :state="validateState('email')"
                aria-describedby="input-1-live-feedback"
              ></b-form-input>
              <b-form-invalid-feedback id="input-1-live-feedback">
                Email is required and a valid email address.
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group
              class="form-group"
              id="example-input-group-2"
              label=""
              label-for="example-input-2"
              ><label class="d1-block">Password</label>
              <b-form-input
                placeholder=""
                class="input password"
                type="password"
                id="example-input-2"
                name="example-input-2"
                v-model="$v.form.password.$model"
                :state="validateState('password')"
                aria-describedby="input-2-live-feedback"
              ></b-form-input>
              <b-form-invalid-feedback id="input-2-live-feedback">
                Password is required.
              </b-form-invalid-feedback>
            </b-form-group>
          </template>

          <template v-else>
            <p class="text-muted text-center mb-3">
              Enter the 6-digit code from your authenticator app.
            </p>
            <b-form-group class="form-group" label-for="mfa-token">
              <label class="d1-block">Authentication Code</label>
              <b-form-input
                id="mfa-token"
                v-model="$v.form.mfaToken.$model"
                placeholder="000000"
                maxlength="6"
                :state="validateState('mfaToken')"
                aria-describedby="mfa-live-feedback"
                autocomplete="one-time-code"
              ></b-form-input>
              <b-form-invalid-feedback id="mfa-live-feedback">
                Please enter a valid 6-digit code.
              </b-form-invalid-feedback>
            </b-form-group>
            <a href="#" class="kt-link" @click.prevent="cancelMfa"
              >Back to login</a
            >
          </template>

          <!--begin::Action-->
          <div class="kt-login__actions login-actions">
            <b-button
              type="submit"
              id="kt_submit"
              class="btn btn-auth btn-primary login-btn-auth btn-elevate kt-login__btn-primary kt-spinner--light kt-spinner--right"
            >
              {{ mfaRequired ? "Verify" : $t("AUTH.LOGIN.BUTTON") }}
            </b-button>

            <router-link
              v-if="!mfaRequired"
              to="/forgotpassword"
              class="kt-link kt-login__link-forgot forgot-password"
            >
              {{ $t("AUTH.FORGOT.TITLE") }}
            </router-link>
          </div>
          <!--end::Action-->
        </b-form>
        <!--end::Form-->
      </div>
      <!--end::Signin-->
    </div>
    <!--end::Body-->
  </div>
</template>

<script>
import "./auth.css";
import { mapState } from "vuex";
import { LOGIN, LOGOUT, CONFIRM_MFA } from "@/store/auth.module";

import { validationMixin } from "vuelidate";
import {
  email,
  minLength,
  maxLength,
  numeric,
  required
} from "vuelidate/lib/validators";

export default {
  mixins: [validationMixin],
  name: "login",
  data() {
    return {
      form: {
        email: null,
        password: null,
        mfaToken: null
      },
      mfaRequired: false,
      pendingUser: null
    };
  },
  validations: {
    form: {
      email: {
        required,
        email
      },
      password: {
        required,
        minLength: minLength(3)
      },
      mfaToken: {
        required,
        numeric,
        minLength: minLength(6),
        maxLength: maxLength(6)
      }
    }
  },
  methods: {
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    resetForm() {
      this.form = { email: null, password: null, mfaToken: null };
      this.mfaRequired = false;
      this.pendingUser = null;
      this.$nextTick(() => {
        this.$v.$reset();
      });
    },
    cancelMfa() {
      this.mfaRequired = false;
      this.pendingUser = null;
      this.form.mfaToken = null;
      this.$v.$reset();
    },
    onSubmit() {
      if (this.mfaRequired) {
        this.$v.form.mfaToken.$touch();
        if (this.$v.form.mfaToken.$anyError) return;
        this.submitMfa();
      } else {
        this.$v.form.email.$touch();
        this.$v.form.password.$touch();
        if (this.$v.form.email.$anyError || this.$v.form.password.$anyError)
          return;
        this.submitLogin();
      }
    },
    submitLogin() {
      const email = this.$v.form.email.$model;
      const password = this.$v.form.password.$model;

      this.$store.dispatch(LOGOUT);

      const submitButton = document.getElementById("kt_submit");
      submitButton.classList.add(
        "kt-spinner",
        "kt-spinner--light",
        "kt-spinner--right"
      );

      this.$store
        .dispatch(LOGIN, { email, password })
        .then(user => {
          if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
            this.$router.push({ name: "confirm-account", params: { email } });
          } else if (
            user.challengeName === "SOFTWARE_TOKEN_MFA" ||
            user.challengeName === "SMS_MFA"
          ) {
            this.pendingUser = user;
            this.mfaRequired = true;
            submitButton.classList.remove(
              "kt-spinner",
              "kt-spinner--light",
              "kt-spinner--right"
            );
          } else {
            this.$router.push({ name: "setup-mfa" });
          }
        })
        .catch(e => {
          this.$logger.error(e);
          submitButton.classList.remove(
            "kt-spinner",
            "kt-spinner--light",
            "kt-spinner--right"
          );
        });
    },
    submitMfa() {
      const submitButton = document.getElementById("kt_submit");
      submitButton.classList.add(
        "kt-spinner",
        "kt-spinner--light",
        "kt-spinner--right"
      );

      this.$store
        .dispatch(CONFIRM_MFA, {
          user: this.pendingUser,
          code: this.form.mfaToken
        })
        .then(() => {
          this.$router.push("/");
        })
        .catch(e => {
          this.$logger.error(e);
          submitButton.classList.remove(
            "kt-spinner",
            "kt-spinner--light",
            "kt-spinner--right"
          );
        });
    }
  },
  computed: {
    ...mapState({
      errors: state => {
        if (state.auth.errors == "not authenticated") return [];

        return state.auth.errors;
      }
    }),
    backgroundImage() {
      return process.env.BASE_URL + "assets/media/bg/bg-4.jpg";
    }
  }
};
</script>
